/**
 * SceneReadonlySections
 *
 * Renders the read-only informational sections of the Scene Properties drawer:
 * Chapter, POV Character, Locations, Goal, Conflict, and Scene Notes.
 */
import { AnySummary, EnrichedEntity, Scene } from '@/lib/types';
import { EntitySummary } from '@common/schemas/entities/summary.schema';
import {
  CompareArrows as ConflictIcon,
  Flag as GoalIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SceneReadonlySectionsProps {
  scene: Scene;
  chapterName: string | undefined;
  povChar: { id: string; name: string } | undefined;
  locations: EntitySummary[];
  onEntityClick: (summary: AnySummary, type: EnrichedEntity['entityType'], event: React.MouseEvent) => void;
  onUpdate: (data: Partial<Scene>) => void;
}

export function SceneReadonlySections({
  scene,
  chapterName,
  povChar,
  locations,
  onEntityClick,
  onUpdate,
}: SceneReadonlySectionsProps) {
  const { t } = useTranslation();

  const [localGoal, setLocalGoal] = useState(scene.goal || '');
  const [localConflict, setLocalConflict] = useState(scene.conflict || '');
  const [localNotes, setLocalNotes] = useState(scene.changesToMake || '');

  // Sync state when props load or change asynchronously
  useEffect(() => {
    setLocalGoal(scene.goal || '');
  }, [scene.goal]);

  useEffect(() => {
    setLocalConflict(scene.conflict || '');
  }, [scene.conflict]);

  useEffect(() => {
    setLocalNotes(scene.changesToMake || '');
  }, [scene.changesToMake]);

  const handleBlur = (field: 'goal' | 'conflict' | 'changesToMake', value: string) => {
    if (scene[field] !== value) {
      onUpdate({ [field]: value });
    }
  };

  return (
    <>
      {/* Chapter */}
      <Box>
        <SectionLabel label={t('editor.chapter')} />
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover', borderStyle: 'dashed' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {chapterName ?? t('editor.unsorted')}
          </Typography>
        </Paper>
      </Box>

      {/* POV */}
      <Box>
        <SectionLabel label={t('editor.pov')} />
        {povChar ? (
          <Paper
            variant="outlined"
            sx={{ p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            onClick={(event) => onEntityClick(povChar as AnySummary, 'characters', event)}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="body2">{povChar.name}</Typography>
            </Stack>
          </Paper>
        ) : (
          <EmptyValue label={t('editor.none')} />
        )}
      </Box>

      {/* Locations */}
      <Box>
        <SectionLabel label={t('editor.location')} />
        {(scene.locationIds ?? []).length > 0 ? (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {(scene.locationIds ?? []).map(id => {
              const loc = locations.find(l => l.id === id);
              return (
                <Chip
                  key={id}
                  icon={<LocationIcon sx={{ fontSize: '16px !important' }} />}
                  label={loc?.name ?? id}
                  size="small"
                  onClick={(event) => loc && onEntityClick(loc, 'locations', event)}
                  sx={{ p: 0.5 }}
                />
              );
            })}
          </Stack>
        ) : (
          <EmptyValue label={t('editor.none')} />
        )}
      </Box>

      {/* Goal */}
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <GoalIcon sx={{ fontSize: 16, color: 'warning.main' }} />
          <SectionLabel label={t('editor.scene_goal')} />
        </Stack>
        <TextField
          multiline
          fullWidth
          minRows={2}
          value={localGoal}
          onChange={(e) => setLocalGoal(e.target.value)}
          onBlur={() => handleBlur('goal', localGoal)}
          placeholder={t('editor.goal_placeholder')}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.default',
            },
          }}
        />
      </Box>

      {/* Conflict */}
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <ConflictIcon sx={{ fontSize: 16, color: 'info.main' }} />
          <SectionLabel label={t('editor.conflict')} />
        </Stack>
        <TextField
          multiline
          fullWidth
          minRows={2}
          value={localConflict}
          onChange={(e) => setLocalConflict(e.target.value)}
          onBlur={() => handleBlur('conflict', localConflict)}
          placeholder={t('editor.conflict_placeholder')}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.default',
            },
          }}
        />
      </Box>

      {/* Scene Notes */}
      <Box>
        <SectionLabel label={t('editor.scene_notes')} />
        <TextField
          multiline
          fullWidth
          minRows={4}
          value={localNotes}
          onChange={(e) => setLocalNotes(e.target.value)}
          onBlur={() => handleBlur('changesToMake', localNotes)}
          placeholder={t('editor.notes_placeholder')}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.default',
              borderLeft: 4,
              borderLeftColor: 'warning.main',
            },
          }}
        />
      </Box>
    </>
  );
}

// ── Private helpers ───────────────────────────────────────────

function SectionLabel({ label }: { label: string }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 600, textTransform: 'uppercase', mb: 1, display: 'block' }}
    >
      {label}
    </Typography>
  );
}

function EmptyValue({ label }: { label: string }) {
  return (
    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
      {label}
    </Typography>
  );
}
